import React, { createContext, useContext, useMemo } from 'react';
import { useAuth } from './AuthContext';

const WishlistContext = createContext();

export const useWishlist = () => {
  const context = useContext(WishlistContext);
  if (!context) {
    throw new Error('useWishlist must be used within WishlistProvider');
  }
  return context;
};

export const WishlistProvider = ({ children }) => {
  const { userDoc, updateUser } = useAuth();

  const wishlistIds = useMemo(() => {
    const ids = Array.isArray(userDoc?.wishlist) ? userDoc.wishlist : [];
    return ids.filter(Boolean).map(String);
  }, [userDoc?.wishlist]);

  const isInWishlist = (productId) => {
    if (!productId) return false;
    return wishlistIds.includes(String(productId));
  };

  const setWishlistIds = async (ids) => {
    const next = Array.isArray(ids) ? Array.from(new Set(ids.map(String))) : [];
    if (!updateUser) throw new Error('Wishlist unavailable');
    await updateUser({ wishlist: next });
    return next;
  };

  const toggleWishlist = async (productId) => {
    if (!productId) return { ids: wishlistIds, added: false };
    const id = String(productId);
    const exists = wishlistIds.includes(id);
    const next = exists ? wishlistIds.filter((x) => x !== id) : [id, ...wishlistIds];
    await setWishlistIds(next);
    return { ids: next, added: !exists };
  };

  const removeFromWishlist = async (productId) => {
    if (!productId) return wishlistIds;
    const id = String(productId);
    const next = wishlistIds.filter((x) => x !== id);
    await setWishlistIds(next);
    return next;
  };

  const value = {
    wishlistIds,
    wishlistCount: wishlistIds.length,
    isInWishlist,
    toggleWishlist,
    removeFromWishlist,
    setWishlistIds,
  };

  return <WishlistContext.Provider value={value}>{children}</WishlistContext.Provider>;
};
